/**
 * Maps source URLs to product logo components.
 *
 * TODO: TREX-805 — Consolidate with recommended-feed to eliminate duplication.
 * This is a copy of platform/packages/townsquare/recommended-feed/src/ui/util/SourceUtil.tsx.
 */
import React from 'react';

import {
	BitbucketIcon,
	ConfluenceIcon,
	GoalsIcon,
	HomeIcon,
	JiraIcon,
	LoomIcon,
	ProjectsIcon,
	TeamsIcon,
} from '@atlaskit/logo';
import { AtlassianIcon } from '@atlaskit/logo/atlassian-icon';
import type { LogoProps } from '@atlaskit/logo/types';

type LogoTypes =
	| 'jira'
	| 'confluence'
	| 'bitbucket'
	| 'loom'
	| 'goals'
	| 'projects'
	| 'teams'
	| 'home'
	| 'unknown';

/** Fallback icon for unrecognized source URLs. */
const UnknownIcon = (): React.JSX.Element => (
	<AtlassianIcon appearance="neutral" label="" size="xxsmall" />
);

const LogoMap: Record<LogoTypes, React.ElementType<LogoProps>> = {
	jira: JiraIcon,
	confluence: ConfluenceIcon,
	bitbucket: BitbucketIcon,
	loom: LoomIcon,
	goals: GoalsIcon,
	projects: ProjectsIcon,
	teams: TeamsIcon,
	home: HomeIcon,
	unknown: UnknownIcon,
};

/** Returns the source type string for a given source URL. */
/* eslint-disable-next-line @atlaskit/platform/one-value-export-per-file -- legacy onboarding baseline */
export function getSourceType(source: string): LogoTypes {
	const lowerSource = source.toLowerCase();

	if (lowerSource.includes('/browse/') || lowerSource.includes('/jira/')) {
		return 'jira';
	}

	if (lowerSource.includes('/wiki/')) {
		return 'confluence';
	}

	if (lowerSource.includes('bitbucket')) {
		return 'bitbucket';
	}

	if (lowerSource.includes('loom.com')) {
		return 'loom';
	}

	if (lowerSource.includes('home.atlassian.com') && lowerSource.includes('/goal/')) {
		return 'goals';
	}

	if (lowerSource.includes('home.atlassian.com') && lowerSource.includes('/project/')) {
		return 'projects';
	}

	if (lowerSource.includes('home.atlassian.com') && lowerSource.includes('/people/')) {
		return 'teams';
	}

	if (lowerSource.includes('home.atlassian.com')) {
		return 'home';
	}

	return 'unknown';
}

/* eslint-disable-next-line @repo/internal/deprecations/deprecation-ticket-required -- TREX-2293 tracks migration to the product-keyed resolver */
/**
 * Returns the appropriate legacy product logo component for a given source type.
 *
 * @deprecated Use `getSourceProductLogo` for new product-keyed consumers. This URL/source-type
 * adapter remains available for legacy source-logo rendering.
 */
/* eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports, @atlaskit/platform/one-value-export-per-file -- legacy onboarding baseline */
export function getLogoForSourceType(sourceType: LogoTypes): React.ElementType<LogoProps> {
	return LogoMap[sourceType];
}
